import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { FiX, FiSave, FiBookmark, FiChevronUp, FiChevronDown, FiPlus, FiAlertTriangle } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { usePrompt } from '../PromptDialog';
import PanelChart from './PanelChart';
import { buildMockData } from './mockPreviewData';
import { parseSelectColumns } from './parseSelectColumns';
import { addNodeFilter, nodeSqlState } from '../../utils/monitoringNodeSql';
import { STORE_ID, sourceLabel, withCurrent } from '../../utils/monitoringSources';
import './PanelEditor.css';

// Y컬럼 색상 선택용 프리셋 팔레트 — 흔히 쓰이는 대시보드 색상.
// "직접 입력" 옵션으로 native color picker 도 fallback 제공.
const COLOR_PALETTE = [
  '#3b82f6', '#10b981', '#f97316', '#a855f7', '#ef4444',
  '#0ea5e9', '#ec4899', '#14b8a6', '#eab308', '#6b7280',
];

// 색상 선택 — swatch 버튼 클릭 시 중앙 모달 팝업 열림.
// Portal 로 document.body 에 렌더 → 상위 overflow:hidden 에 clip 안 됨.
// ESC / overlay 클릭 / 선택 시 자동 닫힘.
function ColorSwatchPicker({ value, onChange }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);
  const handleSelect = (c) => { onChange(c); setOpen(false); };
  return (
    <>
      <button
        type="button"
        className="panel-editor-color-swatch"
        style={{ background: value }}
        onClick={() => setOpen(true)}
        title={t('monitoring:editor.currentColor', { value: value })}
      />
      {open && createPortal(
        <div className="panel-editor-color-modal-overlay" onMouseDown={() => setOpen(false)}>
          <div
            className="panel-editor-color-modal"
            role="dialog"
            aria-label={t('monitoring:editor.pickColor')}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="panel-editor-color-modal-header">
              <span>{t('monitoring:editor.pickColor')}</span>
              <button
                type="button"
                className="panel-editor-color-modal-close"
                onClick={() => setOpen(false)}
                title={t('monitoring:editor.close')}
              ><FiX size={16} /></button>
            </div>
            <div className="panel-editor-color-modal-body">
              <div className="panel-editor-color-modal-palette">
                {COLOR_PALETTE.map(c => (
                  <button
                    key={c}
                    type="button"
                    className={`panel-editor-color-modal-swatch ${c.toLowerCase() === String(value || '').toLowerCase() ? 'panel-editor-color-modal-swatch-active' : ''}`}
                    style={{ background: c }}
                    onClick={() => handleSelect(c)}
                    title={c}
                  />
                ))}
              </div>
              <label className="panel-editor-color-modal-custom">
                <span>{t('monitoring:template.manual')}</span>
                <input
                  type="color"
                  value={value || '#3b82f6'}
                  onChange={(e) => onChange(e.target.value)}
                />
                <input
                  type="text"
                  className="panel-editor-color-modal-hex"
                  value={value || ''}
                  onChange={(e) => onChange(e.target.value)}
                  placeholder="#RRGGBB"
                  maxLength={7}
                />
              </label>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

// t 를 인자로 받는 함수 — 모듈 스코프 상수로 두면 언어를 바꿔도 라벨이 갱신되지 않는다.
const getTickStepOptions = (t) => [
  { value: 0,    label: t('monitoring:editor.auto') },
  { value: 1,    label: '1' },
  { value: 5,    label: '5' },
  { value: 10,   label: '10' },
  { value: 50,   label: '50' },
  { value: 100,  label: '100' },
  { value: 500,  label: '500' },
  { value: 1000, label: '1000' },
];

// fallback 색상 팔레트 (PanelChart 와 동일 — 사용자가 직접 색을 지정하기 전 프리셋)
const FALLBACK_COLORS = ['#3b82f6', '#10b981', '#f97316', '#a855f7', '#ef4444', '#0ea5e9'];

/**
 * 패널 편집 모달 — Grafana 식 일반 시각화 도구.
 *
 * 패널 = (DataSource, SQL, 차트) 트리플. source 분류 없음.
 * 어떤 DBMS 든 동등하게 SQL 작성 → 차트 렌더링.
 */

const getChartTypes = (t) => [
  { value: 'line', label: t('monitoring:chartType.line') },
  { value: 'vbar', label: t('monitoring:chartType.vbar') },
  { value: 'hbar', label: t('monitoring:chartType.hbar') },
  { value: 'grid', label: t('monitoring:chartType.grid') },
  { value: 'merged', label: t('monitoring:chartType.merged') },
  { value: 'job', label: t('monitoring:chartType.job') },
];

/**
 * 미리보기 영역 하단의 "주의" 박스에 표시할 검증 메시지 계산.
 * 실제 저장(handleSave) 의 검증과 같은 항목들이지만, 여기는 alert 로 막지 않고
 * 인라인으로만 안내해 사용자가 실시간 진행 상황을 볼 수 있게 한다.
 */
function computeValidationWarnings(draft, t) {
  if (!draft) return [];
  const w = [];
  if (!draft.title || !String(draft.title).trim()) w.push(t('monitoring:editor.panelTitle'));

  if (draft.chartType === 'merged') {
    if (!Array.isArray(draft.subPanelIds) || draft.subPanelIds.length === 0) {
      w.push(t('monitoring:validate.mergedPick'));
    }
    return w;
  }

  // 그 외 chartType 은 모두 SQL/DataSource 필요
  if (!draft.dataSourceId) w.push(t('monitoring:editor.label.dataSource'));
  if (!draft.sql || !String(draft.sql).trim()) w.push('SQL');

  if (draft.chartType === 'job') {
    if (!draft.jobFields?.name)      w.push(t('monitoring:field.jobNameMapping'));
    if (!draft.jobFields?.startTime) w.push(t('monitoring:field.startTimeMapping'));
  } else {
    // line / vbar / hbar / grid
    if (!draft.xField || !String(draft.xField).trim()) w.push(t('monitoring:field.xColumn'));
    if (!Array.isArray(draft.yFields) || draft.yFields.length === 0) w.push(t('monitoring:field.yColumnsAtLeastOne'));
  }
  return w;
}

/**
 * 'job' 차트의 jobFields 매핑 UI — SQL 결과 컬럼명을 의미 필드에 연결.
 *
 * 필수: name(작업명), startTime(시작시간)
 * 선택: endTime(없으면 진행 중), targetSize/currentSize(진행률·처리량 계산),
 *       targetCount/currentCount (tooltip 표시)
 */
function JobFieldsPicker({ value, onChange }) {
  const { t } = useTranslation();
  const v = value || {};
  const set = (k, val) => onChange({ ...v, [k]: val });
  const f = (k) => v[k] || '';
  return (
    <div className="panel-editor-jobfields">
      <div className="panel-editor-row-compact">
        <label className="panel-editor-label" style={{ flex: 1 }}>
          <span className="panel-editor-label-text">{t('monitoring:field.jobName')}<span className="panel-editor-required">*</span></span>
          <input className="panel-editor-input" type="text" value={f('name')}
            onChange={(e) => set('name', e.target.value)} placeholder={t('monitoring:ph.jobName')} />
        </label>
        <label className="panel-editor-label" style={{ flex: 1 }}>
          <span className="panel-editor-label-text">{t('monitoring:field.startTime')}<span className="panel-editor-required">*</span></span>
          <input className="panel-editor-input" type="text" value={f('startTime')}
            onChange={(e) => set('startTime', e.target.value)} placeholder={t('monitoring:ph.startTs')} />
        </label>
        <label className="panel-editor-label" style={{ flex: 1 }}>
          <span className="panel-editor-label-text">{t('monitoring:field.endTime')}</span>
          <input className="panel-editor-input" type="text" value={f('endTime')}
            onChange={(e) => set('endTime', e.target.value)} placeholder={t('monitoring:notice.blankMeansRunning')} />
        </label>
      </div>
      <div className="panel-editor-row-compact">
        <label className="panel-editor-label" style={{ flex: 1 }}>
          <span className="panel-editor-label-text">{t('monitoring:field.targetSize')}</span>
          <input className="panel-editor-input" type="text" value={f('targetSize')}
            onChange={(e) => set('targetSize', e.target.value)} placeholder={t('monitoring:ph.targetBytes')} />
        </label>
        <label className="panel-editor-label" style={{ flex: 1 }}>
          <span className="panel-editor-label-text">{t('monitoring:field.currentSize')}</span>
          <input className="panel-editor-input" type="text" value={f('currentSize')}
            onChange={(e) => set('currentSize', e.target.value)} placeholder={t('monitoring:ph.currentBytes')} />
        </label>
      </div>
      <div className="panel-editor-row-compact">
        <label className="panel-editor-label" style={{ flex: 1 }}>
          <span className="panel-editor-label-text">{t('monitoring:field.targetCount')}</span>
          <input className="panel-editor-input" type="text" value={f('targetCount')}
            onChange={(e) => set('targetCount', e.target.value)} placeholder={t('monitoring:ph.targetRows')} />
        </label>
        <label className="panel-editor-label" style={{ flex: 1 }}>
          <span className="panel-editor-label-text">{t('monitoring:field.currentCount')}</span>
          <input className="panel-editor-input" type="text" value={f('currentCount')}
            onChange={(e) => set('currentCount', e.target.value)} placeholder={t('monitoring:ph.currentRows')} />
        </label>
      </div>
      <small className="panel-editor-hint">
        {t('monitoring:pe.jobHint1')} <strong>{t('monitoring:editor.size')}</strong> {t('monitoring:pe.jobHint2')}
        <strong>{t('monitoring:field.count')}</strong> {t('monitoring:pe.jobHint3')}
      </small>
    </div>
  );
}

/**
 * 병합 패널의 sub-panel 선택 UI.
 *
 * - 같은 탭의 다른 panel 들 중 chartType !== 'merged' 만 선택 가능 (자기 제외).
 * - 선택된 sub-panel 들은 ordered (위→아래 stack 순서) 리스트로 표시.
 *   ↑/↓ 로 순서 조정, × 로 제거. + 버튼 + select 로 추가.
 * - 선택된 panel 들의 chartType 이 섞여있으면 inline 경고 (저장은 차단 X).
 */
function SubPanelPicker({ tabPanels, excludeId, value, onChange }) {
  const { t } = useTranslation();
  const selectedIds = Array.isArray(value) ? value : [];
  const candidates = (tabPanels || []).filter(p =>
    p.id !== excludeId && p.chartType !== 'merged'
  );
  const candidateById = new Map(candidates.map(p => [p.id, p]));
  const remaining = candidates.filter(p => !selectedIds.includes(p.id));

  // 정렬된 sub-panel 객체들 (dangling reference 는 자체적으로 보존 — id 만 표시)
  const selectedPanels = selectedIds.map(id => candidateById.get(id) || { id, title: t('monitoring:editor.deleted'), chartType: '?', missing: true });

  // chartType 동질성 검사 — 모두 같은 타입인지
  const types = new Set(selectedPanels.filter(p => !p.missing).map(p => p.chartType));
  const mixedTypes = types.size > 1;

  const move = (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= selectedIds.length) return;
    const next = [...selectedIds];
    [next[idx], next[target]] = [next[target], next[idx]];
    onChange(next);
  };
  const remove = (idx) => {
    const next = selectedIds.filter((_, i) => i !== idx);
    onChange(next);
  };
  const add = (id) => {
    if (!id) return;
    if (selectedIds.includes(id)) return;
    onChange([...selectedIds, id]);
  };

  return (
    <div className="panel-editor-subpanel-picker">
      <label className="panel-editor-label">
        <span className="panel-editor-label-text">
          {t('monitoring:pe.subPanelsToMerge')}<span className="panel-editor-required">*</span>
          <span className="panel-editor-hint-inline">
            {t('monitoring:pe.mergeOrderHint')}
          </span>
        </span>
        <div className="panel-editor-subpanel-list">
          {selectedPanels.length === 0 && (
            <div className="panel-editor-subpanel-empty">
              {t('monitoring:pe.noSubPicked')}
            </div>
          )}
          {selectedPanels.map((sub, idx) => (
            <div key={sub.id} className={`panel-editor-subpanel-row${sub.missing ? ' panel-editor-subpanel-row-missing' : ''}`}>
              <span className="panel-editor-subpanel-index">{idx + 1}</span>
              <span className="panel-editor-subpanel-title" title={sub.title}>{sub.title || <em>{t('monitoring:editor.noTitle')}</em>}</span>
              <span className="panel-editor-subpanel-type"><code>{sub.chartType}</code></span>
              <button
                type="button"
                className="panel-editor-btn panel-editor-btn-secondary"
                onClick={() => move(idx, -1)}
                disabled={idx === 0}
                title={t('monitoring:editor.moveUp')}
                style={{ padding: '3px 6px' }}
              ><FiChevronUp size={12} /></button>
              <button
                type="button"
                className="panel-editor-btn panel-editor-btn-secondary"
                onClick={() => move(idx, 1)}
                disabled={idx === selectedPanels.length - 1}
                title={t('monitoring:editor.moveDown')}
                style={{ padding: '3px 6px' }}
              ><FiChevronDown size={12} /></button>
              <button
                type="button"
                className="panel-editor-btn panel-editor-btn-secondary"
                onClick={() => remove(idx)}
                title={t('monitoring:editor.remove')}
                style={{ padding: '3px 8px' }}
              >×</button>
            </div>
          ))}
        </div>
        <div className="panel-editor-subpanel-add">
          <select
            className="panel-editor-input"
            value=""
            onChange={(e) => { add(e.target.value); }}
            disabled={remaining.length === 0}
          >
            <option value="">
              {remaining.length === 0
                ? t('monitoring:sub.none')
                : t('monitoring:sub.pick')}
            </option>
            {remaining.map(p => (
              <option key={p.id} value={p.id}>
                {p.title || t('monitoring:editor.noTitle')} · {p.chartType}
              </option>
            ))}
          </select>
        </div>
        {mixedTypes && (
          <div className="panel-editor-subpanel-warn">
            <FiAlertTriangle size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />
            {t('monitoring:pe.mixedTypes', { types: Array.from(types).join(', ') })}
            {t('monitoring:pe.mixedTypeWarn')}
          </div>
        )}
        <small className="panel-editor-hint">
          {t('monitoring:pe.mergeNote1')}
          {t('monitoring:pe.mergeNote2')}
          {t('monitoring:pe.mergeNote3')}
        </small>
      </label>
    </div>
  );
}

const PanelEditor = ({
  open, panel, slotIndex,
  datasources, queryTemplates, tabPanels, onClose, onSave, onDelete, onSaveTemplate,
  // 노드(RAC) 목록 — 대상의 수집 기록에 있는 inst_id(대시보드가 받아 준다). 비면 고를 노드가 없다
  nodeOptions,
}) => {
  const { t } = useTranslation();
  const prompt = usePrompt();
  const [draft, setDraft] = useState(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  // 폼 영역 활성 탭 — 'fields' (기본) | 'chart' (차트 옵션) | 'vars' (전역변수).
  const [activeSection, setActiveSection] = useState('fields');
  // 노드를 고르며 SQL 을 고쳤으면 무엇을 고쳤는지(그 자리에 보인다) — { maxToSum, keptRatio }
  const [nodeSqlNote, setNodeSqlNote] = useState(null);

  // 새 패널의 읽을 곳은 수집 저장소(2026-10-18 — 데이터소스의 「기본값」 은 없다)
  const defaultDsId = STORE_ID
    || '';

  useEffect(() => {
    if (!open) return;
    setSelectedTemplateId('');
    setNodeSqlNote(null);
    if (panel) {
      setDraft({ ...panel });
    } else {
      setDraft({
        title: '',
        chartType: 'line',
        dataSourceId: defaultDsId,
        sql: '',
        xField: 't',
        yFields: [],
        subPanelIds: [],  // merged 차트 사용 시 채워짐. 다른 chartType 에서는 무시.
        jobFields: {},    // job 차트 사용 시 채워짐. 다른 chartType 에서는 무시.
        enabled: false,   // 신규 패널은 기본 비활성 — 사용자가 카드 토글로 활성화
        ...(typeof slotIndex === 'number' && slotIndex >= 0 ? { slotIndex } : {}),
      });
    }
  }, [open, panel, slotIndex, defaultDsId]);

  const previewData = useMemo(() => draft ? buildMockData(draft) : [], [draft]);

  // 차트 타입 / DS 변경으로 선택된 템플릿이 더 이상 필터에 맞지 않으면 선택 해제
  useEffect(() => {
    if (!selectedTemplateId || !queryTemplates) return;
    const tpl = queryTemplates.find(t => t.id === selectedTemplateId);
    if (!tpl) { setSelectedTemplateId(''); return; }
    const ds = (datasources || []).find(d => d.id === draft?.dataSourceId);
    const dsType = ds?.type || null;
    const tdb = tpl.dbType || 'any';
    const chartMatch = tpl.chartType === draft?.chartType;
    const dbMatch = tdb === 'any' || (dsType && tdb === dsType);
    if (!chartMatch || !dbMatch) setSelectedTemplateId('');
  }, [selectedTemplateId, queryTemplates, draft?.chartType, draft?.dataSourceId, datasources]);

  if (!open || !draft) return null;

  const selectedDs = (datasources || []).find(d => d.id === draft.dataSourceId);
  const dsType = selectedDs?.type || null;

  // 템플릿 필터: 차트 타입 일치 + (dbType=any 이거나 DS.type 일치)
  const templates = (queryTemplates || []).filter(t => {
    if (t.chartType !== draft.chartType) return false;
    const tdb = t.dbType || 'any';
    if (tdb === 'any') return true;
    if (!dsType) return false;
    return tdb === dsType;
  });
  const builtinTemplates = templates.filter(t => t.builtin);
  const userTemplates = templates.filter(t => !t.builtin);

  const updateField = (k, v) => setDraft({ ...draft, [k]: v });
  // 노드 고르기 — 수집 저장소 SQL 에 노드 조건이 없으면 **SQL 에 넣는다**(안 넣으면 골라도 아무 일이 없다). 바뀐 것은 아래 SQL 에 보인다
  const changeNode = (n) => {
    const fixed = addNodeFilter(draft.sql);
    setDraft({ ...draft, node: n, ...(fixed ? { sql: fixed.sql } : {}) });
    if (fixed) setNodeSqlNote({ maxToSum: fixed.maxToSum, keptRatio: fixed.keptRatio });
  };
  const nodeState = nodeSqlState(draft.sql);
  const yFieldsText = (draft.yFields || []).join(', ');

  // 차트 옵션 헬퍼 — 중첩 객체 갱신을 평탄히
  const updateYAxis = (which, key, value) => {
    const axisKey = which === 'right' ? 'yAxisRight' : 'yAxis';
    setDraft({ ...draft, [axisKey]: { ...(draft[axisKey] || {}), [key]: value } });
  };
  const updateYFieldConfig = (fieldName, key, value) => {
    const config = { ...(draft.yFieldsConfig || {}) };
    config[fieldName] = { ...(config[fieldName] || {}), [key]: value };
    setDraft({ ...draft, yFieldsConfig: config });
  };
  const updateProduces = (key, value) => {
    setDraft({ ...draft, produces: { ...(draft.produces || {}), [key]: value } });
  };
  const updateConsume = (idx, key, value) => {
    const list = Array.isArray(draft.consumes) ? [...draft.consumes] : [];
    list[idx] = { ...(list[idx] || {}), [key]: value };
    setDraft({ ...draft, consumes: list });
  };
  const addConsume = () => {
    const list = Array.isArray(draft.consumes) ? [...draft.consumes] : [];
    list.push({ globalVar: '', bindName: '' });
    setDraft({ ...draft, consumes: list });
  };
  const removeConsume = (idx) => {
    const list = Array.isArray(draft.consumes) ? [...draft.consumes] : [];
    list.splice(idx, 1);
    setDraft({ ...draft, consumes: list });
  };

  // 같은 탭의 다른 패널들이 발행하는 전역변수 목록 (consume 드롭다운용).
  // 자기 자신은 제외 — 자기 발행 변수를 자기 SQL 에 직접 매핑할 일은 거의 없으므로.
  const availableGlobalVars = (tabPanels || [])
    .filter(p => p.id !== draft.id && p.produces?.enabled && p.produces?.name)
    .map(p => ({ name: p.produces.name, sourceTitle: p.title }));

  // 보조 Y축 사용 여부 토글 (어떤 yField 라도 axis='right' 면 활성)
  const hasRightAxisField = (draft.yFields || []).some(f => draft.yFieldsConfig?.[f]?.axis === 'right');

  const handleSqlBlur = () => {
    const parsed = parseSelectColumns(draft.sql);
    if (!parsed.xField && parsed.yFields.length === 0) return;
    setDraft(prev => ({
      ...prev,
      xField: parsed.xField || prev.xField,
      yFields: parsed.yFields.length > 0 ? parsed.yFields : prev.yFields,
    }));
  };

  const handleApplyTemplate = (id) => {
    setSelectedTemplateId(id);
    if (!id) return;
    const tpl = templates.find(t => t.id === id);
    if (!tpl) return;
    setDraft({
      ...draft,
      chartType: tpl.chartType,
      sql: tpl.sql || '',
      xField: tpl.xField || draft.xField,
      yFields: Array.isArray(tpl.yFields) ? [...tpl.yFields] : draft.yFields,
      // 'job' 차트 템플릿 — jobFields 매핑도 같이 적용
      ...(tpl.jobFields ? { jobFields: { ...tpl.jobFields } } : {}),
    });
  };

  const handleSaveAsTemplate = async () => {
    if (!onSaveTemplate) return;
    const name = await prompt({
      title: t('monitoring:template.name'),
      message: t('monitoring:template.namePrompt'),
      defaultValue: '',
      placeholder: t('monitoring:ph.chartTitle'),
    });
    if (!name) return;
    const trimmed = name.trim();
    if (!trimmed) return;
    await onSaveTemplate({
      name: trimmed,
      chartType: draft.chartType,
      dbType: dsType || 'any',
      sql: draft.sql || '',
      xField: draft.xField || '',
      yFields: draft.yFields || [],
      // 'job' 차트일 때는 jobFields 도 템플릿에 함께 저장
      ...(draft.chartType === 'job' && draft.jobFields
        ? { jobFields: { ...draft.jobFields } } : {}),
    });
  };

  const handleSave = () => {
    const cleaned = { ...draft };
    if (!cleaned.title) { toast.error(t('monitoring:validate.titleRequired')); return; }

    // 병합 패널 — SQL/X/Y 검증 skip, sub-패널 1개 이상만 검증.
    // chartType mismatch 는 SubPanelPicker 의 inline 경고로만 — 저장 차단 X.
    if (cleaned.chartType === 'merged') {
      if (!Array.isArray(cleaned.subPanelIds) || cleaned.subPanelIds.length === 0) {
        toast.error(t('monitoring:validate.mergedRequired')); return;
      }
      onSave(cleaned);
      return;
    }

    // 작업(job) 패널 — X/Y 대신 jobFields 의 name/startTime 필수.
    if (cleaned.chartType === 'job') {
      if (!cleaned.dataSourceId) { toast.error(t('monitoring:validate.dataSourceRequired')); return; }
      if (!cleaned.sql) { toast.error(t('monitoring:validate.sqlRequired')); return; }
      const jf = cleaned.jobFields || {};
      if (!jf.name)      { toast.error(t('monitoring:validate.jobNameRequired')); return; }
      if (!jf.startTime) { toast.error(t('monitoring:validate.jobStartRequired')); return; }
      onSave(cleaned);
      return;
    }

    // 일반 패널 — 기존 검증
    cleaned.yFields = yFieldsText.split(',').map(s => s.trim()).filter(Boolean);
    if (!cleaned.dataSourceId) { toast.error(t('monitoring:validate.dataSourceRequired')); return; }
    if (!cleaned.sql) { toast.error(t('monitoring:validate.sqlRequired')); return; }
    if (!cleaned.xField) { toast.error(t('monitoring:validate.xRequired')); return; }
    if (cleaned.yFields.length === 0) { toast.error(t('monitoring:validate.yRequired')); return; }
    // yFieldsConfig 에서 현재 yFields 에 없는 stale 엔트리 정리
    if (cleaned.yFieldsConfig) {
      const validNames = new Set(cleaned.yFields);
      const filtered = {};
      for (const [k, v] of Object.entries(cleaned.yFieldsConfig)) {
        if (validNames.has(k)) filtered[k] = v;
      }
      cleaned.yFieldsConfig = filtered;
    }
    onSave(cleaned);
  };

  // 읽을 곳 — 수집 저장소 · 연결(그 연결의 원래 계정으로 돈다 — 관리자만 실행) · 옛 데이터소스
  const sourceOptions = withCurrent(datasources, draft.dataSourceId, t);
  // 목록에 없는 저장값(지운·볼 수 없는 연결)도 「찾을 수 없음」 으로 말한다 — withCurrent 가 더한 줄까지 본다
  const shownSource = sourceOptions.find(x => x.id === draft.dataSourceId);
  const sourceHint = !shownSource ? null
    : shownSource.kind === 'store' ? t('monitoring:src.hint.store')
      : shownSource.kind === 'connection' ? t('monitoring:src.hint.connection')
        : shownSource.kind === 'legacy' ? t('monitoring:src.hint.legacy') : t('monitoring:src.hint.missing');

  return (
    <div className="panel-editor-overlay" onClick={onClose}>
      <div className="panel-editor panel-editor-wide" onClick={(e) => e.stopPropagation()}>
        <div className="panel-editor-header">
          <h3>{panel ? t('monitoring:editor.title') : t('monitoring:editor.newPanel')}</h3>
          <button className="panel-editor-close" onClick={onClose}><FiX size={18} /></button>
        </div>
        <div className="panel-editor-body panel-editor-split">
          {/* ============ 좌측: 입력 폼 ============ */}
          <div className="panel-editor-form">
            {/* ===== 탭 — 기본 / 차트 옵션 / 전역변수 ===== */}
            <div className="panel-editor-tabs" role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={activeSection === 'fields'}
                className={`panel-editor-tab ${activeSection === 'fields' ? 'panel-editor-tab-active' : ''}`}
                onClick={() => setActiveSection('fields')}
              >{t('monitoring:editor.default')}</button>
              <button
                type="button"
                role="tab"
                aria-selected={activeSection === 'chart'}
                className={`panel-editor-tab ${activeSection === 'chart' ? 'panel-editor-tab-active' : ''}`}
                onClick={() => setActiveSection('chart')}
              >{t('monitoring:editor.chartOptions')}</button>
              <button
                type="button"
                role="tab"
                aria-selected={activeSection === 'vars'}
                className={`panel-editor-tab ${activeSection === 'vars' ? 'panel-editor-tab-active' : ''}`}
                onClick={() => setActiveSection('vars')}
              >{t('monitoring:globals.title')}</button>
            </div>

            {/* 기본 탭 — DS / 제목 / 차트타입 / 템플릿 / SQL / X·Y */}
            {activeSection === 'fields' && (
            <div className="panel-editor-form-top">
            <label className="panel-editor-label panel-editor-prominent">
              <span className="panel-editor-label-text">{t('monitoring:src.readFrom')}<span className="panel-editor-required">*</span></span>
              <select
                className="panel-editor-input"
                value={draft.dataSourceId || ''}
                onChange={(e) => updateField('dataSourceId', e.target.value)}
              >
                {sourceOptions.filter(x => x.kind === 'store').map(x => <option key={x.id} value={x.id}>{sourceLabel(x, t)}</option>)}
                {sourceOptions.some(x => x.kind === 'connection') && (
                  <optgroup label={t('monitoring:src.groupConnection')}>
                    {sourceOptions.filter(x => x.kind === 'connection').map(x => <option key={x.id} value={x.id}>{sourceLabel(x, t)}</option>)}
                  </optgroup>
                )}
                {sourceOptions.some(x => x.kind === 'legacy' || x.kind === 'missing') && (
                  <optgroup label={t('monitoring:src.groupOld')}>
                    {sourceOptions.filter(x => x.kind === 'legacy' || x.kind === 'missing').map(x => <option key={x.id} value={x.id}>{x.kind === 'missing' ? x.name : sourceLabel(x, t)}</option>)}
                  </optgroup>
                )}
              </select>
              {sourceHint && <small className="panel-editor-hint">{sourceHint}</small>}
            </label>

            {/* 노드(RAC, 2026-10-18) — 패널마다 정한다. 0 = 합계(노드를 더한 것). SQL 의 :inst_no 로 들어간다(내장 탭·수집 저장소 템플릿이 쓴다) */}
            <label className="panel-editor-label">
              <span className="panel-editor-label-text">{t('monitoring:editor.label.node')}</span>
              <select
                className="panel-editor-input"
                value={String(draft.node || 0)}
                onChange={(e) => changeNode(parseInt(e.target.value, 10) || 0)}
              >
                <option value="0">{t('monitoring:dash.nodeAll')}</option>
                {[...new Set([...(nodeOptions || []), ...(draft.node ? [draft.node] : [])])].sort((a, b) => a - b)
                  .map(n => <option key={n} value={String(n)}>{t('monitoring:dash.node', { n })}</option>)}
              </select>
              <small className="panel-editor-hint">
                {t(`monitoring:editor.nodeState.${nodeState}`)}
              </small>
              {nodeSqlNote && nodeState === 'bound' && (
                <small className="panel-editor-hint panel-editor-node-note">
                  {t('monitoring:editor.nodeSqlChanged', { n: nodeSqlNote.maxToSum })}
                  {nodeSqlNote.keptRatio.length > 0 ? ` ${t('monitoring:editor.nodeRatioKept', { names: nodeSqlNote.keptRatio.join(', ') })}` : ''}
                </small>
              )}
            </label>

            <label className="panel-editor-label">
              <span className="panel-editor-label-text">{t('monitoring:editor.label.title')}<span className="panel-editor-required">*</span></span>
              <input
                className="panel-editor-input"
                type="text"
                value={draft.title || ''}
                onChange={(e) => updateField('title', e.target.value)}
                placeholder={t('monitoring:ph.desc')}
              />
            </label>

            <label className="panel-editor-label">{t('monitoring:pe.chartType')}
              <select className="panel-editor-input" value={draft.chartType} onChange={(e) => updateField('chartType', e.target.value)}>
                {getChartTypes(t).map(ct => <option key={ct.value} value={ct.value}>{ct.label}</option>)}
              </select>
            </label>

            {/* chartType=merged 면 SQL/템플릿/X/Y 입력 hide → SubPanelPicker 만 노출.
                다른 chartType 으로 돌아가면 기존 입력들이 다시 보임 (draft 보존됨). */}
            {draft.chartType === 'merged' ? (
              <SubPanelPicker
                tabPanels={tabPanels}
                excludeId={draft.id}
                value={draft.subPanelIds || []}
                onChange={(ids) => updateField('subPanelIds', ids)}
              />
            ) : (
            <>
            <label className="panel-editor-label">
              <span>
                {t('monitoring:pe.template')} <span className="panel-editor-hint-inline">
                  {t('monitoring:pe.chartParen')} <code>{draft.chartType}</code>
                  {dsType ? <> · DBMS <code>{dsType}</code></> : t('monitoring:globals.noDbms')}
                  {t('monitoring:pe.totalCount', { n: templates.length })}
                </span>
              </span>
              <select
                className="panel-editor-input"
                value={selectedTemplateId}
                onChange={(e) => handleApplyTemplate(e.target.value)}
              >
                <option value="">{t('monitoring:template.orPick')}</option>
                {templates.length === 0 && (
                  <option disabled>{t('monitoring:template.noMatch')}</option>
                )}
                {builtinTemplates.length > 0 && (
                  <optgroup label={t('monitoring:template.builtin')}>
                    {builtinTemplates.map(tpl => (
                      <option key={tpl.id} value={tpl.id} title={tpl.description || ''}>
                        {tpl.name}
                      </option>
                    ))}
                  </optgroup>
                )}
                {userTemplates.length > 0 && (
                  <optgroup label={t('monitoring:template.user')}>
                    {userTemplates.map(tpl => (
                      <option key={tpl.id} value={tpl.id} title={tpl.description || ''}>
                        {tpl.name}
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>
              {selectedTemplateId && (
                <small className="panel-editor-hint">
                  <strong>{t('monitoring:editor.label.descColon')}</strong>{' '}
                  {templates.find(t => t.id === selectedTemplateId)?.description || t('monitoring:editor.noDesc')}
                </small>
              )}
              <small className="panel-editor-hint">
                {t('monitoring:pe.templateHint')}
              </small>
            </label>

            <label className="panel-editor-label">
              <span className="panel-editor-label-text">SQL<span className="panel-editor-required">*</span></span>
              <span className="panel-editor-hint-inline">{t('monitoring:globals.bindPrefix')} <code>:minutes</code>, <code>:hours</code> {t('monitoring:globals.availableSuffix')}</span>
              <textarea
                className="panel-editor-textarea"
                rows={8}
                placeholder={t('monitoring:ph.sql')}
                value={draft.sql || ''}
                onChange={(e) => updateField('sql', e.target.value)}
                onBlur={handleSqlBlur}
              />
              <small className="panel-editor-hint">
                {t('monitoring:pe.sqlHint')}
              </small>
            </label>

            {draft.chartType === 'job' ? (
              /* 작업 차트 — X/Y 컬럼 대신 jobFields 매핑 UI */
              <JobFieldsPicker
                value={draft.jobFields || {}}
                onChange={(jf) => updateField('jobFields', jf)}
              />
            ) : (
              <div className="panel-editor-row">
                <label className="panel-editor-label" style={{ flex: 1 }}>
                  <span className="panel-editor-label-text">{t('monitoring:field.xColumnFull')}<span className="panel-editor-required">*</span></span>
                  <input className="panel-editor-input" type="text" value={draft.xField || ''} onChange={(e) => updateField('xField', e.target.value)} placeholder={t('monitoring:ph.xField')} />
                </label>
                <label className="panel-editor-label" style={{ flex: 2 }}>
                  <span className="panel-editor-label-text">{t('monitoring:field.yColumnsFull')}<span className="panel-editor-required">*</span></span>
                  <input
                    className="panel-editor-input"
                    type="text"
                    value={yFieldsText}
                    onChange={(e) => setDraft({ ...draft, yFields: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
                    placeholder={t('monitoring:ph.yFields')}
                  />
                </label>
              </div>
            )}
            </>
            )}
            </div>
            )}

            {activeSection === 'chart' && (
              <div className="panel-editor-section">
                <div className="panel-editor-section-content">
                  {/* 표시 — 제목 숨김 / 정렬 / 범례 위치 */}
                  <div className="panel-editor-subsection">
                    <div className="panel-editor-subsection-title">{t('monitoring:editor.show')}</div>
                    <label className="panel-editor-checkbox-label">
                      <input
                        type="checkbox"
                        checked={!!draft.hideTitle}
                        onChange={(e) => updateField('hideTitle', e.target.checked)}
                      />
                      <span>{t('monitoring:editor.hideTitleOnCard')}</span>
                    </label>
                    <div className="panel-editor-row-compact">
                      <label className="panel-editor-label" style={{ flex: 1 }}>{t('monitoring:pe.titleAlign')}
                        <select
                          className="panel-editor-input"
                          value={draft.titleAlign || 'left'}
                          onChange={(e) => updateField('titleAlign', e.target.value)}
                          disabled={!!draft.hideTitle}
                        >
                          <option value="left">{t('monitoring:editor.left')}</option>
                          <option value="center">{t('monitoring:editor.mid')}</option>
                          <option value="right">{t('monitoring:editor.right')}</option>
                        </select>
                      </label>
                      <label className="panel-editor-label" style={{ flex: 1 }}>{t('monitoring:pe.legendPos')}
                        <select
                          className="panel-editor-input"
                          value={draft.legendPosition || 'bottom'}
                          onChange={(e) => updateField('legendPosition', e.target.value)}
                        >
                          <option value="bottom">{t('monitoring:editor.belowChart')}</option>
                          <option value="top">{t('monitoring:editor.aboveChart')}</option>
                        </select>
                      </label>
                    </div>
                    <div className="panel-editor-row-compact">
                      <label className="panel-editor-label" style={{ flex: 1 }}>{t('monitoring:pe.legendAlign')}
                        <select
                          className="panel-editor-input"
                          value={draft.legendAlign || 'center'}
                          onChange={(e) => updateField('legendAlign', e.target.value)}
                        >
                          <option value="left">{t('monitoring:editor.left')}</option>
                          <option value="center">{t('monitoring:editor.middle')}</option>
                          <option value="right">{t('monitoring:editor.right')}</option>
                        </select>
                      </label>
                    </div>
                  </div>

                  {/* Y축 (왼쪽) — 한 줄로 압축 */}
                  <div className="panel-editor-subsection">
                    <div className="panel-editor-subsection-title">{t('monitoring:editor.yAxis')}</div>
                    <div className="panel-editor-row-compact">
                      <label className="panel-editor-label" style={{ flex: 2 }}>{t('monitoring:editor.label.title')}
                        <input
                          className="panel-editor-input"
                          type="text"
                          value={draft.yAxis?.title || ''}
                          onChange={(e) => updateYAxis('left', 'title', e.target.value)}
                          placeholder={t('monitoring:ph.sessions')}
                        />
                      </label>
                      <label className="panel-editor-label" style={{ flex: 1 }}>{t('monitoring:pe.unit')}
                        <input
                          className="panel-editor-input"
                          type="text"
                          value={draft.yAxis?.unit || ''}
                          onChange={(e) => updateYAxis('left', 'unit', e.target.value)}
                          placeholder="MB, %"
                        />
                      </label>
                      <label className="panel-editor-label" style={{ flex: 1 }}>{t('monitoring:pe.ticks')}
                        <select
                          className="panel-editor-input"
                          value={draft.yAxis?.tickStep || 0}
                          onChange={(e) => updateYAxis('left', 'tickStep', parseInt(e.target.value, 10) || 0)}
                        >
                          {getTickStepOptions(t).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      </label>
                    </div>
                    <div className="panel-editor-row-compact" style={{ gap: 14 }}>
                      <label className="panel-editor-checkbox-label">
                        <input
                          type="checkbox"
                          checked={draft.yAxis?.showTitle !== false}
                          onChange={(e) => updateYAxis('left', 'showTitle', e.target.checked)}
                        />
                        <span>{t('monitoring:editor.showTitle')}</span>
                      </label>
                      <label className="panel-editor-checkbox-label">
                        <input
                          type="checkbox"
                          checked={draft.yAxis?.showUnit !== false}
                          onChange={(e) => updateYAxis('left', 'showUnit', e.target.checked)}
                        />
                        <span>{t('monitoring:editor.unitLabel')}</span>
                      </label>
                    </div>
                  </div>

                  {/* 보조 Y축 — 자동 활성 (Y컬럼별 설정에서 right 매핑 시) */}
                  <div className="panel-editor-subsection" style={{ opacity: hasRightAxisField ? 1 : 0.55 }}>
                    <div className="panel-editor-subsection-title">
                      {t('monitoring:pe.secondaryY')}
                      <span className="panel-editor-hint-inline">
                        {hasRightAxisField ? t('monitoring:editor.active') : t('monitoring:notice.rightAxisHint')}
                      </span>
                    </div>
                    <div className="panel-editor-row-compact">
                      <label className="panel-editor-label" style={{ flex: 2 }}>{t('monitoring:editor.label.title')}
                        <input
                          className="panel-editor-input"
                          type="text"
                          value={draft.yAxisRight?.title || ''}
                          onChange={(e) => updateYAxis('right', 'title', e.target.value)}
                          placeholder={t('monitoring:ph.ratio')}
                          disabled={!hasRightAxisField}
                        />
                      </label>
                      <label className="panel-editor-label" style={{ flex: 1 }}>{t('monitoring:pe.unit')}
                        <input
                          className="panel-editor-input"
                          type="text"
                          value={draft.yAxisRight?.unit || ''}
                          onChange={(e) => updateYAxis('right', 'unit', e.target.value)}
                          placeholder="%"
                          disabled={!hasRightAxisField}
                        />
                      </label>
                      <label className="panel-editor-label" style={{ flex: 1 }}>{t('monitoring:pe.ticks')}
                        <select
                          className="panel-editor-input"
                          value={draft.yAxisRight?.tickStep || 0}
                          onChange={(e) => updateYAxis('right', 'tickStep', parseInt(e.target.value, 10) || 0)}
                          disabled={!hasRightAxisField}
                        >
                          {getTickStepOptions(t).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      </label>
                    </div>
                  </div>

                  {/* Y 컬럼별 색상 + 축 매핑 — 별도 스크롤 영역 */}
                  <div className="panel-editor-subsection" style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                    <div className="panel-editor-subsection-title">
                      {t('monitoring:pe.perYSettings')}
                      <span className="panel-editor-hint-inline">{t('monitoring:pe.countSuffix', { n: (draft.yFields || []).length })}</span>
                    </div>
                    {(draft.yFields || []).length === 0 ? (
                      <small className="panel-editor-hint">{t('monitoring:validate.yFirst')}</small>
                    ) : (
                      <div className="panel-editor-yfield-scroll">
                        <table className="panel-editor-yfield-table">
                          <thead>
                            <tr>
                              <th>{t('monitoring:editor.label.name')}</th>
                              <th style={{ width: 50 }}>{t('monitoring:editor.color')}</th>
                              <th style={{ width: 96 }}>{t('monitoring:editor.axis')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(draft.yFields || []).map((f, idx) => {
                              const cfg = draft.yFieldsConfig?.[f] || {};
                              const color = cfg.color || FALLBACK_COLORS[idx % FALLBACK_COLORS.length];
                              return (
                                <tr key={f}>
                                  <td><code>{f}</code></td>
                                  <td>
                                    <ColorSwatchPicker
                                      value={color}
                                      onChange={(c) => updateYFieldConfig(f, 'color', c)}
                                    />
                                  </td>
                                  <td>
                                    <select
                                      className="panel-editor-input-compact"
                                      value={cfg.axis || 'left'}
                                      onChange={(e) => updateYFieldConfig(f, 'axis', e.target.value)}
                                    >
                                      <option value="left">{t('monitoring:editor.left')}</option>
                                      <option value="right">{t('monitoring:editor.right')}</option>
                                    </select>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                </div>
              </div>
            )}

            {activeSection === 'vars' && (
              <div className="panel-editor-section">
                <div className="panel-editor-section-content">
                  {/* 전역변수 제공 (Produce) — 이 패널 클릭 시 갱신될 탭 전역변수 정의 */}
                  <div className="panel-editor-subsection">
                    <div className="panel-editor-subsection-title">
                      {t('monitoring:pe.produce')}
                      <span className="panel-editor-hint-inline">{t('monitoring:globals.isProducer')}</span>
                    </div>
                    <label className="panel-editor-checkbox-label">
                      <input
                        type="checkbox"
                        checked={!!draft.produces?.enabled}
                        onChange={(e) => updateProduces('enabled', e.target.checked)}
                      />
                      <span>{t('monitoring:globals.registerProducer')}</span>
                    </label>
                    <div className="panel-editor-row-compact">
                      <label className="panel-editor-label" style={{ flex: 2 }}>{t('monitoring:pe.varName')}
                        <input
                          className="panel-editor-input"
                          type="text"
                          value={draft.produces?.name || ''}
                          onChange={(e) => updateProduces('name', e.target.value.replace(/[^a-zA-Z0-9_]/g, ''))}
                          placeholder={t('monitoring:ph.selectedEvent')}
                          disabled={!draft.produces?.enabled}
                          maxLength={40}
                        />
                      </label>
                      <label className="panel-editor-label" style={{ flex: 2 }}>{t('monitoring:pe.initialValue')}
                        <input
                          className="panel-editor-input"
                          type="text"
                          value={draft.produces?.initialValue ?? ''}
                          onChange={(e) => updateProduces('initialValue', e.target.value)}
                          placeholder={t('monitoring:globals.waitPlaceholder')}
                          disabled={!draft.produces?.enabled}
                        />
                      </label>
                      <label className="panel-editor-label" style={{ flex: 1 }}>{t('monitoring:pe.clickExtract')}
                        <select
                          className="panel-editor-input"
                          value={draft.produces?.extract || 'x'}
                          onChange={(e) => updateProduces('extract', e.target.value)}
                          disabled={!draft.produces?.enabled}
                        >
                          <option value="x">{t('monitoring:field.xValue')}</option>
                          <option value="y">{t('monitoring:field.yValue')}</option>
                          <option value="series">{t('monitoring:field.yColumnName')}</option>
                        </select>
                      </label>
                    </div>
                    <small className="panel-editor-hint">
                      {t('monitoring:pe.produceHint')}
                    </small>
                  </div>

                  {/* 전역변수 사용 (Consume) — 다른 패널이 발행한 변수를 SQL 바인드에 매핑 */}
                  <div className="panel-editor-subsection">
                    <div className="panel-editor-subsection-title">
                      {t('monitoring:pe.consume')}
                      <span className="panel-editor-hint-inline">{t('monitoring:globals.isConsumer')}</span>
                    </div>
                    {(draft.consumes || []).length === 0 && (
                      <small className="panel-editor-hint">
                        {availableGlobalVars.length === 0
                          ? t('monitoring:globals.noProducer')
                          : t('monitoring:globals.addHint')}
                      </small>
                    )}
                    {(draft.consumes || []).map((c, idx) => (
                      <div key={idx} className="panel-editor-row-compact">
                        <label className="panel-editor-label" style={{ flex: 2 }}>{t('monitoring:globals.title')}
                          <select
                            className="panel-editor-input"
                            value={c.globalVar || ''}
                            onChange={(e) => updateConsume(idx, 'globalVar', e.target.value)}
                          >
                            <option value="">{t('monitoring:editor.selectPlaceholder')}</option>
                            {availableGlobalVars.map(v => (
                              <option key={v.name} value={v.name}>
                                {v.name}{v.sourceTitle ? ` (← ${v.sourceTitle})` : ''}
                              </option>
                            ))}
                            {/* 이미 선택돼 있지만 producer 가 없어진 경우(이름 변경/삭제) 그대로 표시 */}
                            {c.globalVar && !availableGlobalVars.some(v => v.name === c.globalVar) && (
                              <option value={c.globalVar}>{c.globalVar} {t('monitoring:pe.noProducer')}</option>
                            )}
                          </select>
                        </label>
                        <label className="panel-editor-label" style={{ flex: 2 }}>{t('monitoring:pe.sqlBind')}
                          <input
                            className="panel-editor-input"
                            type="text"
                            value={c.bindName || ''}
                            onChange={(e) => updateConsume(idx, 'bindName', e.target.value.replace(/[^a-zA-Z0-9_]/g, ''))}
                            placeholder={t('monitoring:globals.bindPlaceholder')}
                            maxLength={40}
                          />
                        </label>
                        <button
                          type="button"
                          className="panel-editor-btn panel-editor-btn-secondary"
                          onClick={() => removeConsume(idx)}
                          title={t('monitoring:editor.delete')}
                          style={{ alignSelf: 'flex-end', padding: '5px 10px', fontSize: 12 }}
                        >×</button>
                      </div>
                    ))}
                    <button
                      type="button"
                      className="panel-editor-btn panel-editor-btn-secondary"
                      onClick={addConsume}
                      style={{ alignSelf: 'flex-start', padding: '5px 10px', fontSize: 12 }}
                      disabled={availableGlobalVars.length === 0 && (draft.consumes || []).length === 0}
                    >{t('monitoring:globals.add')}</button>
                    {(draft.consumes || []).length > 0 && (
                      <small className="panel-editor-hint">
                        {t('monitoring:globals.usageHintPre')} <code>:{t('monitoring:globals.bindNameWord')}</code>{' '}
                        {t('monitoring:globals.usageHintMid')} <code>WHERE event = :{(draft.consumes[0]?.bindName) || 'evt'}</code>.{' '}
                        {t('monitoring:globals.usageHintPost')}
                      </small>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ============ 우측: 미리보기 ============ */}
          <div className="panel-editor-preview">
            <div className="panel-editor-preview-header">
              <span>{t('monitoring:editor.preview')}</span>
              <small>{t('monitoring:notice.sampleData')}</small>
            </div>
            <div className="panel-editor-preview-body">
              {!draft.hideTitle && (
                <div
                  className="panel-editor-preview-title"
                  style={{ textAlign: draft.titleAlign || 'left' }}
                >
                  {draft.title?.trim() || t('monitoring:editor.noTitle')}
                </div>
              )}
              {draft.chartType === 'merged' ? (
                <div className="panel-editor-preview-merged">
                  <strong>{t('monitoring:sub.mergedPreview')}</strong>
                  <div>
                    {t('monitoring:pe.subPicked')}: <code>{(draft.subPanelIds || []).length}</code>
                  </div>
                  <small>
                    {t('monitoring:pe.previewNote1')}
                    {t('monitoring:pe.previewNote2')}
                  </small>
                </div>
              ) : (
                <PanelChart
                  panel={{
                    chartType: draft.chartType,
                    xField: draft.xField || 'x',
                    yFields: (draft.yFields && draft.yFields.length > 0) ? draft.yFields : ['series_1'],
                    // 차트 옵션 전달 — 미리보기에 즉시 반영
                    yAxis: draft.yAxis,
                    yAxisRight: draft.yAxisRight,
                    yFieldsConfig: draft.yFieldsConfig,
                    legendPosition: draft.legendPosition,
                    legendAlign: draft.legendAlign,
                    // 'job' 차트 — 매핑 안 된 필드는 mockPreviewData 와 동일 default 컬럼명
                    // 으로 폴백해서 미리보기는 항상 mock 으로 그려짐. 실제 검증은 아래
                    // 주의 영역에서 별도로 표시.
                    ...(draft.chartType === 'job' ? {
                      jobFields: {
                        name:         draft.jobFields?.name         || 'name',
                        startTime:    draft.jobFields?.startTime    || 'start_time',
                        endTime:      draft.jobFields?.endTime      || 'end_time',
                        targetSize:   draft.jobFields?.targetSize   || 'target_size',
                        currentSize:  draft.jobFields?.currentSize  || 'current_size',
                        targetCount:  draft.jobFields?.targetCount  || 'target_count',
                        currentCount: draft.jobFields?.currentCount || 'current_count',
                      },
                    } : {}),
                  }}
                  data={previewData}
                  height={240}
                  isDarkMode={document.documentElement.getAttribute('data-theme') === 'dark'}
                />
              )}
              <div className="panel-editor-preview-meta">
                <div><strong>{t('monitoring:src.readFromColon')}</strong> {selectedDs ? sourceLabel(selectedDs, t) : t('monitoring:editor.unset')}</div>
                <div><strong>{t('monitoring:editor.label.chartColon')}</strong> {draft.chartType}</div>
                {draft.hideTitle && (
                  <div><strong>{t('monitoring:editor.label.titleColon')}</strong> {t('monitoring:editor.hiddenOnCard')}</div>
                )}
              </div>
              {/* 저장 전 검증 결과 — 미리보기는 mock 으로 항상 표시되지만, 실제
                  저장에 필요한 필수 항목이 채워지지 않으면 여기에 t('monitoring:notice.caution') 로 안내. */}
              {(() => {
                const w = computeValidationWarnings(draft, t);
                if (w.length === 0) return null;
                return (
                  <div className="panel-editor-preview-warnings">
                    <div className="panel-editor-preview-warnings-title">
                      {t('monitoring:pe.warnBeforeSave')}
                    </div>
                    <ul>
                      {w.map((msg, i) => <li key={i}>{msg}</li>)}
                    </ul>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>

        <div className="panel-editor-footer">
          {panel && onDelete && (
            <button className="panel-editor-btn panel-editor-btn-danger" onClick={() => onDelete(panel)}>{t('monitoring:editor.delete')}</button>
          )}
          {onSaveTemplate && (
            <button
              className="panel-editor-btn panel-editor-btn-secondary"
              onClick={handleSaveAsTemplate}
              title={t('monitoring:template.saveHint')}
            >
              <FiBookmark size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
              {t('monitoring:pe.saveAsTemplate')}
            </button>
          )}
          <div style={{ flex: 1 }} />
          <button className="panel-editor-btn" onClick={onClose}>{t('monitoring:editor.cancel')}</button>
          <button className="panel-editor-btn panel-editor-btn-primary" onClick={handleSave}>
            <FiSave size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
            {t('monitoring:pe.save')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PanelEditor;
